export const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ===== 部署环境绊线 =====
// 构建时打进包里的 API_BASE 是"按分支各管一摊"的,曾因 dev→main 合并
// 带错导致正式前端把商家请求打进测试后端(2026-10 事故)。合并驱动与
// config-guard 工作流都拦不住快进推送 —— 这是最后一道运行时防线:
// 部署域名与包内 API 环境不匹配时直接拒绝渲染(App.js 最先调用),
// 宁可明确报错也绝不把这个环境商家的请求泄给另一个环境。
// 未知域名(本地 localhost、CF 预览地址、未来新环境)不拦截,避免误伤。
const ENV_PAIRS = {
  'app.flexcover.app': 'https://api.flexcover.app',
  'protection-plain-api-test.blyship.com': 'https://protection-plain-api-test.tlack.cn'
};

// 返回 null = 环境匹配(或不做检查);否则返回不匹配的细节供报错展示。
export function deploymentMismatch(hostname, apiBase) {
  const expected = ENV_PAIRS[hostname];
  if (!expected || apiBase === expected) return null;
  return { expected, apiBase };
}

// 真实店铺域名始终来自 URL 的 ?shop= 参数(见 src/auth.jsx 的
// shopifyUrlParams)。这个写死的域名只在本地开发(直接打开 localhost:3000、
// 不在 Shopify iframe 里)时使用:嵌入模式下后端从 session token(JWT)解析
// 店铺,请求里不传任何店铺标识;本地模式下以 ?shop= 传给后端。它永远不会
// 被发送到生产环境的嵌入会话里,也不会在生产数据库中创建店铺。
export const SHOP_DOMAIN = 'demo-shop.myshopify.com';

export const PLAN_TYPES = [
  { label: 'Shipping Protection', value: 'shipping_protection' },
  { label: 'Warranty', value: 'warranty' },
  { label: 'Return Insurance', value: 'return_insurance' },
  { label: 'Custom Protection', value: 'custom' }
];

export const PLAN_TYPE_CONFIG = {
  shipping_protection: { label: 'Shipping', badgeTone: 'info', icon: '🚚' },
  warranty: { label: 'Warranty', badgeTone: 'success', icon: '🔧' },
  return_insurance: { label: 'Return', badgeTone: 'warning', icon: '↩️' },
  custom: { label: 'Custom', badgeTone: 'new', icon: '🛡️' }
};

export const PRICE_TYPES = [
  { label: 'Fixed Amount', value: 'fixed' },
  { label: 'Percentage of Order', value: 'percentage' }
];

// 选中哪个 icon，隐藏产品就用哪张 CDN 图片（后端 models/plan_icon.go 维护同一份映射）。
// key 统一用去掉变体选择符(U+FE0F)的裸码点，iconKey() 负责归一化后再查。
export const PLAN_ICON_IMAGES = {
  '🚚': 'https://cdn.flexcover.app/img/truck.png',
  '🔧': 'https://cdn.flexcover.app/img/wrench.png',
  '↩': 'https://cdn.flexcover.app/img/refresh.png',
  '🛡': 'https://cdn.flexcover.app/img/shield.png',
  '📦': 'https://cdn.flexcover.app/img/package.png'
};

// 选择器只展示上面三个默认图标(加自定义上传共四张)。🔧/↩ 已从选择器
// 收起,但渲染映射保留 —— 存量计划可能仍存着这两个图标,显示不能断。
export const ICON_CHOICES = ['🚚', '🛡', '📦'];

// icon 归一化：码点名("U+21A9")解析成字符，再去掉 U+FE0F 变体选择符。
export function iconKey(icon) {
  if (!icon) return '';
  const resolved = resolvePlanIcon(icon) || icon;
  return resolved.replace(/\uFE0F/g, '');
}

// 自定义图标：icon 直接存上传后的图片 URL(POST /api/uploads 返回)。
export function isIconUrl(icon) {
  return typeof icon === 'string' && /^https?:\/\//i.test(icon.trim());
}

export const DEFAULT_SETTINGS = {
  widgetTitle: 'Protect Your Order',
  widgetDescription: 'Add protection for peace of mind',
  widgetPosition: 'cart_drawer',
  primaryColor: '#008060',
  accentColor: '#FBF7ED',
  autoSelectFirstPlan: false,
  showIcons: true,
  showDescriptions: true,
  showBanner: false,
  showProtectionSubtotal: false,
  widgetAnchors: {}
};

export const WIDGET_POSITIONS = [
  { label: 'Cart drawer', value: 'cart_drawer' },
  { label: 'Cart page', value: 'cart_page' },
  { label: 'Checkout (Plus only)', value: 'checkout' }
];

// How the widget lands relative to the configured insertion anchor.
export const ANCHOR_POSITIONS = [
  { label: 'Before anchor', value: 'before' },
  { label: 'After anchor', value: 'after' },
  { label: 'Inside, first', value: 'prepend' },
  { label: 'Inside, last', value: 'append' }
];

// Anchor keys editable in Settings; a store with all-empty anchors relies
// on the widget's built-in theme presets + auto-detection.
export const ANCHOR_FIELDS = [
  { key: 'themeName', label: 'Theme name', placeholder: 'e.g. Dawn', helpText: 'Informational only — records which theme these selectors were written for. Auto-detection matches popular themes by name.' },
  { key: 'cartContainer', label: 'Cart container (抽屉容器)', placeholder: 'e.g. cart-drawer, .cart-drawer', helpText: 'The cart drawer root the widget may mount into. The /cart page has its own container field below.' },
  { key: 'widgetAnchor', label: 'Insertion anchor (插入锚点)', placeholder: 'e.g. .drawer__footer button[name="checkout"]', helpText: 'Exact element the widget is placed relative to (drawer and cart page, unless the cart page has its own anchor).' },
  { key: 'subtotalAnchor', label: 'Subtotal anchor (总价)', placeholder: 'e.g. .totals__subtotal-value', helpText: 'Fallback insertion point: below the subtotal row when no insertion anchor matches.' },
  { key: 'checkoutAnchor', label: 'Checkout anchor (结账)', placeholder: 'e.g. [name="checkout"]', helpText: 'Checkout button; the widget is placed right above it when no insertion anchor is set.' },
  { key: 'cartPageContainer', label: 'Cart page container (购物车页容器)', placeholder: 'e.g. #main-cart-footer, form[action*="/cart"]', helpText: 'Root element of the /cart page — the widget mounts inside it, separate from the drawer.' },
  { key: 'cartPageWidgetAnchor', label: 'Cart page insertion anchor (购物车页锚点)', placeholder: 'e.g. .cart__ctas button[name="checkout"]', helpText: 'Where the widget lands on the /cart page; overrides the shared insertion anchor there. Falls back to the page checkout button when empty.' }
];

// Every anchor key persisted to the backend: the text fields above plus the
// two position selects (before/after/prepend/append, one per surface). Kept
// in one place so the save payload, the theme preset payload and the dirty
// check all cover the same set — a key missing here is silently dropped.
export const ANCHOR_PERSIST_KEYS = [
  ...ANCHOR_FIELDS.map(({ key }) => key),
  'widgetPosition',
  'cartPageWidgetPosition'
];

// Cart-write timing (WidgetAnchorConfig.type): how a checked plan reaches the
// cart. 'auto' follows the theme-fit library row (default PartialRender).
export const CART_WRITE_TIMING_OPTIONS = [
  { value: 'auto', label: 'Auto (follow theme-fit library)' },
  { value: '1', label: 'On toggle — real cart line immediately (type 1)' },
  { value: '2', label: 'At checkout — write when Checkout is clicked (type 2)' }
];

export function formatMoney(value) {
  const n = parseFloat(value) || 0;
  return `$${n.toFixed(2)}`;
}

// Seed data stores icons as codepoint names like "U+1F527"; render real emoji.
export function resolvePlanIcon(icon) {
  if (!icon) return null;
  const match = /^U\+([0-9A-Fa-f]{4,6})$/.exec(icon.trim());
  if (match) return String.fromCodePoint(parseInt(match[1], 16));
  return icon;
}

// icon（emoji、U+xxxx 码点名或自定义图片 URL）→ 展示用图片 URL；
// 没有映射返回 null（调用方回退渲染 emoji 字符）。
export function planImage(icon) {
  if (!icon) return null;
  if (isIconUrl(icon)) return icon.trim();
  return PLAN_ICON_IMAGES[iconKey(icon)] || null;
}

export function formatPriceBadge(plan) {
  if (!plan) return '';
  if (plan.priceType === 'percentage') {
    let label = `${plan.priceValue}% of order`;
    if (plan.minPrice != null && plan.maxPrice != null) {
      label += ` (${formatMoney(plan.minPrice)}–${formatMoney(plan.maxPrice)})`;
    } else if (plan.minPrice != null) {
      label += ` (min ${formatMoney(plan.minPrice)})`;
    } else if (plan.maxPrice != null) {
      label += ` (max ${formatMoney(plan.maxPrice)})`;
    }
    return label;
  }
  return formatMoney(plan.priceValue);
}
